import {
  createAsyncThunk,
  createSlice,
  type PayloadAction
} from "@reduxjs/toolkit"

import * as plansEstimateApi from "../api/services.ts"
import { type InboxCategory } from "../api/services.ts"
import { type SowDocument } from "../api/services.types.ts"

interface PlansEstimateState {
  activeDocument: SowDocument | null
  inbox: SowDocument[]
  inboxCount: number
  categoryCounts: Record<InboxCategory, number> | null
  // The category and search the rows answer: "<category>|<q>".
  inboxQuery?: string
  status: "idle" | "loading" | "failed"
  // The list request whose answer may land: an older one (a search typed
  // over) answering later is dropped.
  inboxRequest?: string
}

const initialState: PlansEstimateState = {
  activeDocument: null,
  inbox: [],
  inboxCount: 0,
  categoryCounts: null,
  status: "idle"
}

interface CreateAndUploadResult {
  document: SowDocument
  pageCount: number
}

// A single thunk for "create the document, then upload its plan source" —
// the two backend calls (POST /plans-estimate/ then POST /plans-estimate/{id}/source/)
// are one user action (picking a file in UploadScreen) and always happen
// together, so there's no reason for a caller to sequence them itself.
// uploadPlanSource's own response only carries { id, page_count } (no full
// document), so the document is re-fetched once both calls succeed to give
// the caller a complete, current SowDocument alongside the page count.
export const createAndUploadPlanThunk = createAsyncThunk(
  "plansEstimate/createAndUpload",
  async ({
    title,
    file
  }: {
    title: string
    file: File
  }): Promise<CreateAndUploadResult> => {
    const created = await plansEstimateApi.createDocument(title)
    const { page_count: pageCount } = await plansEstimateApi.uploadPlanSource(
      created.id,
      file
    )
    const document = await plansEstimateApi.getDocument(created.id)
    return { document, pageCount }
  }
)

export const fetchDocument = createAsyncThunk(
  "plansEstimate/fetch",
  async (id: string) => plansEstimateApi.getDocument(id)
)

export const INBOX_PAGE_SIZE = 20

// `page` 1 replaces the list; a later page is appended ("Show more"). A refresh
// re-reads as many rows as are already shown, so the list does not shrink.
export const fetchInbox = createAsyncThunk(
  "plansEstimate/fetchInbox",
  async (
    args:
      | {
          page?: number
          pageSize?: number
          q?: string
          category?: InboxCategory
        }
      | undefined
  ) => {
    const page = args?.page ?? 1
    const response = await plansEstimateApi.listDocuments({
      page,
      page_size: args?.pageSize ?? INBOX_PAGE_SIZE,
      ...(args?.q ? { q: args.q } : {}),
      ...(args?.category ? { category: args.category } : {})
    })
    return { ...response, page }
  }
)

const plansEstimateSlice = createSlice({
  name: "plansEstimate",
  initialState,
  reducers: {
    // Used by this slice's own test, and available to any screen that
    // already holds a fresh document and wants to seed activeDocument
    // without a redundant re-fetch.
    setActiveDocumentForTest(state, action: PayloadAction<SowDocument>) {
      state.activeDocument = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAndUploadPlanThunk.fulfilled, (state, action) => {
        state.activeDocument = action.payload.document
      })
      .addCase(fetchDocument.fulfilled, (state, action) => {
        state.activeDocument = action.payload
      })
      .addCase(fetchInbox.pending, (state, action) => {
        state.status = "loading"
        state.inboxRequest = action.meta.requestId
      })
      .addCase(fetchInbox.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.inboxRequest) return
        const { page, results } = action.payload
        state.inbox =
          page === 1
            ? results
            : [
                ...state.inbox,
                ...results.filter(
                  (row) => !state.inbox.some((shown) => shown.id === row.id)
                )
              ]
        state.inboxCount = action.payload.count
        state.categoryCounts = action.payload.category_counts ?? null
        state.inboxQuery = `${action.meta.arg?.category ?? ""}|${action.meta.arg?.q ?? ""}`
        state.status = "idle"
      })
      .addCase(fetchInbox.rejected, (state, action) => {
        if (action.meta.requestId !== state.inboxRequest) return
        state.status = "failed"
      })
  }
})

export const { setActiveDocumentForTest } = plansEstimateSlice.actions
export default plansEstimateSlice.reducer
